import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatbot } from '../../context/ChatbotContext';
import { MessageSquare, X, Minimize2, Send, Maximize2 } from 'lucide-react';
import Button from '../ui/Button';
import clsx from 'clsx';

const Chatbot: React.FC = () => {
  const { t } = useTranslation();
  const { chatState, isOpen, toggleChat, closeChat, sendMessage, setChatState, getMessagesLeft } = useChatbot();
  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatState.messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (messageInput.trim()) {
      sendMessage(messageInput);
      setMessageInput('');
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={toggleChat}
        className="fixed bottom-6 right-6 z-50 p-4 bg-primary text-white rounded-full shadow-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-300"
        aria-label={t('chatbot.openLabel')}
      >
        <MessageSquare size={24} />
      </button>
    );
  }

  return (
    <div className={clsx(
      "fixed z-50 bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 animate-fade-in transition-all duration-300",
      chatState.isExpanded 
        ? "top-[10vh] left-[10vw] right-[10vw] bottom-[10vh]"
        : "bottom-6 right-6 w-full max-w-md h-[500px]"
    )}>
      {/* Chatbot Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-primary text-white">
        <div className="flex items-center space-x-2">
          <MessageSquare size={20} />
          <h3 className="text-sm">{t('chatbot.title')}</h3>
          <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
            {getMessagesLeft()} {t('chatbot.messagesLeft')}
          </span>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setChatState(prev => ({ ...prev, isExpanded: !prev.isExpanded }))}
            className="p-1.5 hover:bg-primary-dark rounded-full transition-colors"
            aria-label={chatState.isExpanded ? t('chatbot.minimizeLabel') : t('chatbot.expandLabel')}
          >
            {chatState.isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <button
            onClick={closeChat}
            className="p-1.5 hover:bg-primary-dark rounded-full transition-colors"
            aria-label={t('chatbot.closeLabel')}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {chatState.messages.map((message) => (
          <div
            key={message.id}
            className={`mb-4 ${
              message.isUser ? 'flex justify-end' : 'flex justify-start'
            }`}
          >
            <div
              className={`max-w-[75%] p-3 rounded-lg ${
                message.isUser
                  ? 'bg-primary text-white rounded-tr-none'
                  : 'bg-white text-gray-800 border border-gray-200 rounded-tl-none'
              }`}
            >
              <p className="whitespace-pre-line">{message.content}</p>
              <span
                className={`text-xs block mt-1 ${
                  message.isUser ? 'text-primary-light' : 'text-gray-500'
                }`}
              >
                {message.timestamp instanceof Date 
                  ? message.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : new Date(message.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                }
              </span>
            </div>
          </div>
        ))}

        {chatState.isLoading && (
          <div className="flex justify-start mb-4">
            <div className="bg-white text-gray-800 p-3 rounded-lg rounded-tl-none border border-gray-200">
              <div className="flex space-x-2">
                <div className="w-2 h-2 rounded-full bg-gray-400 animate-pulse"></div>
                <div className="w-2 h-2 rounded-full bg-gray-400 animate-pulse delay-150"></div>
                <div className="w-2 h-2 rounded-full bg-gray-400 animate-pulse delay-300"></div>
              </div>
            </div>
          </div>
        )}

        {chatState.error && (
          <div className="p-3 mb-4 bg-error-light text-error-dark rounded-lg text-sm">
            {chatState.error}
          </div>
        )}

        <div ref={messagesEndRef}></div>
      </div>

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="p-3 border-t border-gray-200">
        <div className="flex space-x-2">
          <input
            type="text"
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            placeholder={t('chatbot.placeholder')}
            className="input flex-1"
            disabled={chatState.isLoading}
          />
          <Button
            type="submit"
            variant="primary"
            disabled={chatState.isLoading || !messageInput.trim()}
            icon={<Send size={18} />}
          >
            {t('chatbot.send')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Chatbot;